import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, FileDown, Printer, Save, Trash2, X } from "lucide-react";
import { API } from "../lib/api";

const sectionOptions = [
  ["details", "Daftar kategori dan uraian pengajuan"],
  ["summary", "Ringkasan anggaran"],
  ["period", "Periode"],
  ["receipts", "Lampiran foto nota"],
  ["items", "Lampiran bukti pembelian barang"],
];
const REPORT_HEADER_EMPTY = { job: "Operasional Bulanan Kantor", location: "Fuel Terminal Kijang", logo_left: "", logo_right: "", manager_title: "Manager", supervisor_title: "Supervisor" };

const pdfErrorMessage = async error => {
  const body = error.response?.data;
  if (body instanceof Blob) {
    try {
      const text = await body.text();
      const payload = JSON.parse(text);
      if (payload?.detail) return payload.detail;
    } catch { /* Keep the friendly fallback for non-JSON server responses. */ }
  } else if (typeof body === "string") {
    try {
      const payload = JSON.parse(body);
      if (payload?.detail) return payload.detail;
    } catch { /* Keep the friendly fallback for non-JSON server responses. */ }
  } else if (body?.detail) {
    return body.detail;
  }
  return "Pratinjau PDF gagal dibuat. Coba lagi; jika tetap gagal, hubungi administrator.";
};

export const PdfExport = ({ month }) => {
  const [sections, setSections] = useState(["details", "period", "receipts", "items"]);
  const [photoOrientation, setPhotoOrientation] = useState("portrait");
  const [headerSettings, setHeaderSettings] = useState(REPORT_HEADER_EMPTY);
  const [headerLoading, setHeaderLoading] = useState(false);
  const [headerSaving, setHeaderSaving] = useState(false);
  const [headerVersion, setHeaderVersion] = useState(0);
  const [open, setOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const frameRef = useRef(null);

  const toggleSection = value => setSections(current => current.includes(value) ? current.filter(item => item !== value) : [...current, value]);

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setHeaderLoading(true);
    axios.get(`${API}/settings/report-header`)
      .then(({ data }) => { if (active) setHeaderSettings({ ...REPORT_HEADER_EMPTY, ...data }); })
      .catch(err => { if (active) toast.error(err.response?.data?.detail || "Gagal memuat Header Laporan"); })
      .finally(() => { if (active) setHeaderLoading(false); });
    return () => { active = false; };
  }, [open]);

  const selectLogo = (key, file) => {
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) { toast.error("Logo harus berupa JPG, PNG, atau WEBP"); return; }
    if (file.size > 1.5 * 1024 * 1024) { toast.error("Ukuran setiap logo maksimal 1,5 MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setHeaderSettings(current => ({ ...current, [key]: String(reader.result || "") }));
    reader.onerror = () => toast.error("Logo gagal dibaca");
    reader.readAsDataURL(file);
  };

  const saveHeaderSettings = async event => {
    event.preventDefault();
    setHeaderSaving(true);
    try {
      const { data } = await axios.put(`${API}/settings/report-header`, headerSettings);
      setHeaderSettings(current => ({ ...current, ...data }));
      setHeaderVersion(version => version + 1);
      window.dispatchEvent(new Event("notifications-updated"));
      toast.success("Header Laporan berhasil disimpan");
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal menyimpan Header Laporan"); }
    finally { setHeaderSaving(false); }
  };

  useEffect(() => {
    if (!open || !sections.length) {
      setPreviewUrl("");
      setLoading(false);
      setError("");
      return undefined;
    }
    const controller = new AbortController();
    let objectUrl = "";
    setPreviewUrl("");
    setLoading(true);
    setError("");
    const timer = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/reports/pdf`, { params: { month, sections: sections.join(","), photo_orientation: photoOrientation }, responseType: "blob", timeout: 120000, signal: controller.signal });
        objectUrl = URL.createObjectURL(data);
        setPreviewUrl(objectUrl);
      } catch (err) {
        if (!controller.signal.aborted) setError(await pdfErrorMessage(err));
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, 250);
    return () => {
      clearTimeout(timer);
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [open, month, sections, photoOrientation, headerVersion]);

  const download = () => {
    if (!previewUrl) return;
    const a = document.createElement("a");
    a.href = previewUrl; a.download = `laporan-${month}.pdf`; a.click();
    toast.success("Laporan PDF berhasil diunduh");
  };

  const print = () => {
    if (!previewUrl) return;
    const frameWindow = frameRef.current?.contentWindow;
    if (frameWindow) {
      frameWindow.focus();
      frameWindow.print();
    } else {
      const printWindow = window.open(previewUrl, "_blank");
      if (!printWindow) { toast.error("Izinkan pop-up browser untuk mencetak PDF"); return; }
      printWindow.onload = () => printWindow.print();
    }
  };

  return <>
    <button data-testid="report-pdf-open" className="primary-btn" onClick={() => setOpen(true)}><FileDown size={16} /> Buat PDF</button>
    {open && <div className="pdf-preview-backdrop" role="presentation" onMouseDown={e => { if (e.target === e.currentTarget) setOpen(false); }}>
      <section className="pdf-preview-dialog" role="dialog" aria-modal="true" aria-labelledby="pdf-preview-title">
        <header className="pdf-preview-header"><div><p className="eyebrow">LAPORAN {month}</p><h2 id="pdf-preview-title">Pratinjau PDF</h2><p className="muted">Atur isi laporan, periksa hasilnya, lalu unduh atau cetak.</p></div><button type="button" className="icon-btn" aria-label="Tutup pratinjau" onClick={() => setOpen(false)}><X size={20} /></button></header>
        <div className="pdf-preview-layout">
          <aside className="pdf-preview-controls">
            <details className="pdf-report-settings">
              <summary><strong>Bagian laporan</strong><span>Atur bagian dan tampilan foto</span></summary>
              <fieldset className="pdf-preview-sections"><legend>Pilih bagian</legend>{sectionOptions.map(([value, label]) => <label key={value}><input type="checkbox" checked={sections.includes(value)} onChange={() => toggleSection(value)} />{label}</label>)}</fieldset>
              <label className="pdf-filter-label">Orientasi foto lampiran
                <select data-testid="report-pdf-photo-orientation" value={photoOrientation} onChange={event => setPhotoOrientation(event.target.value)}><option value="portrait">Potret</option><option value="landscape">Landscape</option></select>
              </label>
            </details>
            <details className="pdf-report-settings pdf-header-accordion">
              <summary><strong>Header Laporan</strong><span>Atur logo, lokasi, dan jabatan tanda tangan</span></summary>
              <form className="pdf-header-settings" onSubmit={saveHeaderSettings}>
                {headerLoading ? <p className="muted">Memuat pengaturan header...</p> : <>
                  <div className="pdf-header-logo-grid">{[["logo_left", "Logo kiri"], ["logo_right", "Logo kanan"]].map(([key, label]) => <label className="report-logo-field" key={key}>{label}<input type="file" accept="image/png,image/jpeg,image/webp" onChange={event => { selectLogo(key, event.target.files?.[0]); event.target.value = ""; }} /><small>JPG, PNG, WEBP · maks. 1,5 MB</small>{headerSettings[key] && <div className="report-logo-preview"><img src={headerSettings[key]} alt={`Pratinjau ${label.toLowerCase()}`} /><button type="button" className="secondary-btn" onClick={() => setHeaderSettings(current => ({ ...current, [key]: "" }))}><Trash2 size={13} /> Hapus</button></div>}</label>)}</div>
                  <label>Lokasi<input required maxLength={120} value={headerSettings.location} onChange={event => setHeaderSettings(current => ({ ...current, location: event.target.value }))} placeholder="Contoh: Fuel Terminal Kijang" /></label>
                  <label>Jabatan Manager<input required maxLength={80} value={headerSettings.manager_title} onChange={event => setHeaderSettings(current => ({ ...current, manager_title: event.target.value }))} placeholder="Contoh: Manager" /></label>
                  <label>Jabatan Supervisor<input required maxLength={80} value={headerSettings.supervisor_title} onChange={event => setHeaderSettings(current => ({ ...current, supervisor_title: event.target.value }))} placeholder="Contoh: Supervisor" /></label>
                  <button type="submit" className="primary-btn" disabled={headerSaving}><Save size={14} /> {headerSaving ? "Menyimpan..." : "Simpan Header Laporan"}</button>
                </>}
              </form>
            </details>
            <div className="pdf-preview-actions">
              <button data-testid="report-export-pdf" className="primary-btn" onClick={download} disabled={!previewUrl || loading}><Download size={16} /> Unduh PDF</button>
              <button data-testid="report-print" className="secondary-btn" onClick={print} disabled={!previewUrl || loading}><Printer size={16} /> Cetak PDF</button>
            </div>
            {!sections.length && <p className="pdf-preview-hint">Pilih setidaknya satu bagian laporan untuk membuat pratinjau.</p>}
          </aside>
          <div className="pdf-preview-document" aria-live="polite">
            {loading && <div className="pdf-preview-message">Sedang menyiapkan pratinjau...</div>}
            {!loading && error && <div className="pdf-preview-message error">{error}</div>}
            {!loading && !error && !sections.length && <div className="pdf-preview-message">Pilih bagian laporan untuk melihat pratinjau.</div>}
            {previewUrl && !loading && <iframe ref={frameRef} title="Pratinjau dokumen laporan PDF" src={previewUrl} />}
          </div>
        </div>
      </section>
    </div>}
  </>;
};
